import React from 'react';
import { ClerkProvider, SignIn, SignUp, useUser, useClerk, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';

/**
 * Clerk Authentication Context Replacement
 * 
 * This file replaces the old Supabase Auth context with Clerk.
 * It provides similar hooks and patterns for compatibility with existing code.
 */

// Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY environment variable. Please set it in Railway dashboard.');
}

// Type assertion after validation - we know it's a string now
const clerkPublishableKey: string = CLERK_PUBLISHABLE_KEY;

// Re-export Clerk hooks with familiar names
export { useUser, useClerk };

/**
 * useAuth hook - provides authentication state and methods
 * Replaces the old Supabase useAuth hook
 */
export function useAuth() {
  const { user, isLoaded, isSignedIn } = useUser();
  const { signOut: clerkSignOut, openSignIn, openSignUp } = useClerk();
  const { getToken } = useClerkAuth();

  return {
    user: user ? {
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress || '',
      user_metadata: {
        full_name: user.fullName || '',
      },
      app_metadata: {
        provider: user.externalAccounts?.[0]?.provider || 'email',
      },
      is_anonymous: false, // Clerk doesn't support anonymous users
    } : null,
    session: user ? { user } : null,
    loading: !isLoaded,
    isAuthenticated: isSignedIn || false,
    signInWithOAuth: async (provider: string) => {
      // Clerk handles this via UI components
      openSignIn();
    },
    signOut: async () => {
      await clerkSignOut();
    },
    getAccessToken: async () => {
      // Get Clerk session token for Supabase
      try {
        const token = await getToken({ template: 'supabase' });
        return token || undefined;
      } catch (error) {
        console.error('Error getting Clerk token:', error);
        return undefined;
      }
    },
    // Expose Clerk modal methods for components to use
    openSignIn,
    openSignUp,
  };
}

/**
 * ClerkAuthProvider - Wraps the app with Clerk authentication
 * Replace the old AuthProvider with this
 */
export function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <ClerkProvider 
      publishableKey={clerkPublishableKey}
      afterSignOutUrl="/"
      signInFallbackRedirectUrl="/app"
      signUpFallbackRedirectUrl="/app"
    >
      {children}
    </ClerkProvider>
  );
}

/**
 * RequireAuth component - protects routes requiring authentication
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Sign In Page Component
 */
export function SignInPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      <SignIn 
        routing="path" 
        path="/login"
        signUpUrl="/signup"
        afterSignInUrl="/app"
      />
    </div>
  );
}

/**
 * Sign Up Page Component
 */
export function SignUpPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      <SignUp 
        routing="path" 
        path="/signup"
        signInUrl="/login"
        afterSignUpUrl="/app"
      />
    </div>
  );
}

/**
 * UserButton component - shows user avatar and menu
 * Replace UserProfileDropdown with this
 */
export { UserButton } from '@clerk/clerk-react';
